/**
 *  @swagger
 *  definitions:
 *    RegattaResultTableEntryParams:
 *      type: object
 *      properties:
 *        RegattaResultTableId:
 *          description: ID of the associated RegattaResultTable
 *          type: string
 *        RegattaEntryId:
 *          description: ID of the associated RegattaEntry
 *          type: string
 *        Place:
 *          description: Place of the RegattaResultTableEntry
 *          type: string
 *        Points:
 *          description: Points of the RegattaResultTableEntry
 *          type: integer
 *
 *  @swagger
 *  definitions:
 *    RegattaResultTableEntryCreateParams:
 *      allOf:
 *        - $ref: '#/definitions/RegattaResultTableEntryParams'
 *        - type: object
 *          required:
 *            - RegattaResultTableId
 *            - RegattaEntryId
 *            - Points
 *
 *  @swagger
 *  definitions:
 *    RegattaResultTableEntryQueryParams:
 *      allOf:
 *        - type: object
 *          properties:
 *            id:
 *              description: ID of the RegattaResultTableEntry
 *              type: string
 *        - $ref: '#/definitions/RegattaResultTableEntryParams'
 *
 *  @swagger
 *  definitions:
 *    RegattaResultTableEntryUpdateParams:
 *      allOf:
 *        - $ref: '#/definitions/RegattaResultTableEntryQueryParams'
 *        - type: object
 *          required:
 *            - id
 *
 *  @swagger
 *  definitions:
 *    RegattaResultTableEntry:
 *      allOf:
 *        - $ref: '#/definitions/RegattaResultTableEntryCreateParams'
 *        - $ref: '#/definitions/RegattaResultTableEntryUpdateParams'
 *        - $ref: '#/definitions/Timestamps'
 *
 *  @swagger
 *  components:
 *    schemas:
 *      RegattaResultTableEntry:
 *        allOf:
 *          - $ref: '#/definitions/RegattaResultTableEntry'
 *          - type: object
 *            properties:
 *              RegattaResultTable:
 *                description: RegattaResultTable of the RegattaResultTableEntry
 *                $ref: '#/components/schemas/RegattaResultTable'
 *              RegattaEntry:
 *                description: RegattaEntry of the RegattaResultTableEntry
 *                $ref: '#/components/schemas/RegattaEntry'
 *              RegattaResultTableRaceResults:
 *                description: RegattaResultTableRaceResults of the RegattaResultTableEntry
 *                type: array
 *                items:
 *                  $ref: '#/components/schemas/RegattaResultTableRaceResult'
 */

// Types
import type {
  $Model,
  $ModelClass,
  $ModelClasses,
} from '.';
import type {
  $Field,
} from '../types';

export type $RelationIds = {
  RegattaEntryId: string;
  RegattaResultTableId: string;
};

export type $Params = {
  Place: null | string;
  Points: number;
};

export type $CreateParams = $Params & $RelationIds;

export type $UpdateParams = {
  id: string;
} & Partial<$CreateParams>;

export type $UpsertParams = $CreateParams & {
  id?: string;
};

export type $QueryParams = {
  id?: null | string;
} & Partial<$CreateParams>;

export type $Associations = {
  RegattaEntry?: $Model<'RegattaEntry'>;
  RegattaResultTable?: $Model<'RegattaResultTable'>;
  RegattaResultTableRaceResults?: Array<$Model<'RegattaResultTableRaceResult'>>;
};

export type $ModelParams = $Associations & $CreateParams & {
  id: string;
};

type $Fields = $Params & {
  id: string;
};

const fields = (
  Sequelize,
): Record<keyof $Fields, $Field> => ({
  id: {
    allowNull: false,
    defaultValue: Sequelize.UUIDV4,
    primaryKey: true,
    type: Sequelize.UUID,
    unique: true,
  },
  Place: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Points: {
    allowNull: false,
    type: Sequelize.DOUBLE(
      4,
      1,
    ),
  },
});

export default ((
  Sequelize,
  stack,
): $ModelClass<'RegattaResultTableEntry'> => {
  const RegattaResultTableEntry = stack.define(
    'RegattaResultTableEntry',
    fields(Sequelize),
    {
      tableName: 'RegattaResultTableEntries',
    },
  );

  RegattaResultTableEntry.associate = (models: $ModelClasses) => {
    models.RegattaResultTableEntry.belongsTo(
      models.RegattaResultTable,
      {
        as: 'RegattaResultTable',
        foreignKey: 'RegattaResultTableId',
      },
    );
    models.RegattaResultTableEntry.belongsTo(
      models.RegattaEntry,
      {
        as: 'RegattaEntry',
        foreignKey: 'RegattaEntryId',
      },
    );
    models.RegattaResultTableEntry.hasMany(
      models.RegattaResultTableRaceResult,
      {
        as: 'RegattaResultTableRaceResults',
        foreignKey: 'RegattaResultTableEntryId',
      },
    );
  };

  RegattaResultTableEntry.addScope(
    'defaultScope',
    {
      order: [['createdAt', 'ASC']],
    },
  );

  return RegattaResultTableEntry;
});
